/**
 * GPGPUParticleField
 * 
 * GPGPU particle terrain using ping-pong WebGLRenderTargets.
 * Simulation shader updates particle positions via noise displacement.
 * Display shader renders particles as gl.POINTS with depth-based sizing.
 * 
 * CRITICAL: This component MUST be placed inside an existing r3f <Canvas>.
 * It is NOT a standalone Canvas — it is a scene child.
 * 
 * Mouse tracking: window mousemove → useRef (zero React state)
 * Scroll velocity: from ScrollVelocityContext ref
 * Ping-pong order: swap → bind previous → render to current
 */

import React, { useRef, useMemo, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { useScrollVelocity } from '../../context/ScrollVelocityContext';

// ─── SIMULATION VERTEX (simple passthrough) ───
const simVertexShader = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 1.0);
}
`;

// ─── SIMULATION FRAGMENT (GPGPU position update with Ashima noise) ───
const simFragmentShader = `
precision highp float;

uniform float uTime;
uniform vec2 uMouse;
uniform float uScrollVelocity;
uniform float uLowAmp;
uniform float uHighAmp;
uniform float uMouseRadius;
uniform float uMouseForce;
varying vec2 vUv;

// --- Ashima Arts 3D Simplex Noise (full) ---
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
  vec4 m = max(0.5 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 105.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

void main() {
  vec2 uv = vUv;

  float x = (uv.x - 0.5) * 8.0;
  float z = (uv.y - 0.5) * 8.0;

  // Scroll-reactive high frequency amplitude (uniform-driven base)
  float scrollAmp = uHighAmp + uScrollVelocity * 0.85;

  // Low frequency noise — terrain base (uniform-driven amplitude)
  float lowFreq = snoise(vec3(x * 0.4, z * 0.4, uTime * 0.15)) * uLowAmp;

  // High frequency noise — spiky detail
  float highFreq = snoise(vec3(x * 1.8, z * 1.8, uTime * 0.4)) * scrollAmp;

  float y = lowFreq + highFreq;

  // Mouse interaction — uniform-driven radius and force
  float mouseDist = length(uv - uMouse);
  float mouseInfluence = 1.0 - smoothstep(0.0, uMouseRadius, mouseDist);
  y += mouseInfluence * uMouseForce;

  float phase = fract(uTime * 0.1 + uv.x * 3.7 + uv.y * 2.3);

  gl_FragColor = vec4(x, y, z, phase);
}
`;

// ─── DISPLAY VERTEX SHADER ───
const displayVertexShader = `
precision highp float;

uniform sampler2D uPositions;
uniform float uPointSize;
uniform float uMinAlpha;
uniform float uMaxAlpha;

attribute vec2 reference;

varying float vOpacity;

void main() {
  vec4 pos = texture2D(uPositions, reference);

  vec4 mvPosition = modelViewMatrix * vec4(pos.xyz, 1.0);
  gl_Position = projectionMatrix * mvPosition;

  // Depth-based point size
  float depth = -mvPosition.z;
  gl_PointSize = uPointSize * (3.0 / max(depth, 0.5));
  gl_PointSize = clamp(gl_PointSize, 0.8, 3.0);

  // Opacity driven by Y height — uniform-driven range
  vOpacity = smoothstep(-1.0, 2.5, pos.y) * (uMaxAlpha - uMinAlpha) + uMinAlpha;
}
`;

// ─── DISPLAY FRAGMENT SHADER ───
const displayFragmentShader = `
precision mediump float;

varying float vOpacity;

void main() {
  float d = length(gl_PointCoord - 0.5);
  if (d > 0.5) discard;
  float alpha = (1.0 - smoothstep(0.3, 0.5, d)) * vOpacity;
  gl_FragColor = vec4(1.0, 1.0, 1.0, alpha);
}
`;

// ─── GPGPU PARTICLE FIELD COMPONENT ───
const GPGPUParticleField = () => {
    const { gl } = useThree();
    const velocityRef = useScrollVelocity();
    const smoothedVelocity = useRef(0);

    // Mouse tracking — window mousemove → ref, zero React state
    const mouseRef = useRef({ x: 0.5, y: 0.5 });

    useEffect(() => {
        const handleMouseMove = (e) => {
            mouseRef.current.x = e.clientX / window.innerWidth;
            mouseRef.current.y = 1.0 - (e.clientY / window.innerHeight); // flip Y for GL
        };
        window.addEventListener('mousemove', handleMouseMove, { passive: true });
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Determine texture size — mobile gets far fewer particles to stay at 60fps
    const texSize = useMemo(() => {
        const isMobile = window.innerWidth <= 768;
        if (isMobile) return 80; // 6,400 particles on mobile vs 80,100 on desktop
        const maxSize = gl.capabilities.maxTextureSize;
        return maxSize >= 4096 ? 283 : 200;
    }, [gl]);

    const particleCount = texSize * texSize;

    // ─── PING-PONG RENDER TARGETS ───
    const rtA = useMemo(() => new THREE.WebGLRenderTarget(texSize, texSize, {
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
        type: THREE.FloatType,
        depthBuffer: false,
        stencilBuffer: false,
    }), [texSize]);

    const rtB = useMemo(() => new THREE.WebGLRenderTarget(texSize, texSize, {
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
        type: THREE.FloatType,
        depthBuffer: false,
        stencilBuffer: false,
    }), [texSize]);

    // Track which RT is current vs previous
    const pingPong = useRef({ current: rtA, previous: rtB });

    // ─── SIMULATION MATERIAL ───
    const simMaterial = useMemo(() => new THREE.ShaderMaterial({
        vertexShader: simVertexShader,
        fragmentShader: simFragmentShader,
        uniforms: {
            uTime: { value: 0 },
            uMouse: { value: new THREE.Vector2(0.5, 0.5) },
            uScrollVelocity: { value: 0 },
            uLowAmp: { value: 1.2 },
            uHighAmp: { value: 0.35 },
            uMouseRadius: { value: 0.25 },
            uMouseForce: { value: 0.8 },
        },
        depthWrite: false,
        depthTest: false,
    }), []);

    // ─── SIMULATION MESH (off-screen quad) ───
    const simScene = useMemo(() => {
        const scene = new THREE.Scene();
        const geometry = new THREE.PlaneGeometry(2, 2);
        const mesh = new THREE.Mesh(geometry, simMaterial);
        scene.add(mesh);
        return scene;
    }, [simMaterial]);

    const simCamera = useMemo(() => {
        const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
        return cam;
    }, []);

    // ─── DISPLAY GEOMETRY — reference UVs ───
    const { displayGeometry, displayMaterial } = useMemo(() => {
        const geo = new THREE.BufferGeometry();

        // Reference attribute: UV coordinates into the simulation texture
        const references = new Float32Array(particleCount * 2);
        for (let i = 0; i < texSize; i++) {
            for (let j = 0; j < texSize; j++) {
                const idx = (i * texSize + j) * 2;
                references[idx] = (j + 0.5) / texSize;
                references[idx + 1] = (i + 0.5) / texSize;
            }
        }
        geo.setAttribute('reference', new THREE.BufferAttribute(references, 2));

        // Dummy position attribute required by Three.js
        const positions = new Float32Array(particleCount * 3);
        geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

        const mat = new THREE.ShaderMaterial({
            vertexShader: displayVertexShader,
            fragmentShader: displayFragmentShader,
            uniforms: {
                uPositions: { value: null },
                uPointSize: { value: 1.8 },
                uMinAlpha: { value: 0.06 },
                uMaxAlpha: { value: 0.35 },
            },
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.AdditiveBlending,
        });

        return { displayGeometry: geo, displayMaterial: mat };
    }, [particleCount, texSize]);

    // Camera auto-rotation angle
    const cameraAngle = useRef(0);
    const cameraTargetRotX = useRef(0);
    const cameraTargetRotY = useRef(0);

    // ─── FRAME LOOP ───
    useFrame((state, delta) => {
        // --- SCROLL VELOCITY → smooth ref ---
        const rawVelocity = Math.abs(velocityRef.current || 0);
        const normalizedVelocity = Math.min(rawVelocity / 3000, 1.0);
        const decayFactor = Math.exp(-delta / 0.8);
        smoothedVelocity.current = Math.max(
            normalizedVelocity,
            smoothedVelocity.current * decayFactor
        );

        // --- UPDATE SIMULATION UNIFORMS ---
        simMaterial.uniforms.uTime.value += delta;
        simMaterial.uniforms.uMouse.value.set(
            mouseRef.current.x,
            mouseRef.current.y
        );
        simMaterial.uniforms.uScrollVelocity.value = smoothedVelocity.current;

        // --- PING-PONG: swap FIRST, then render ---
        const pp = pingPong.current;
        const temp = pp.current;
        pp.current = pp.previous;
        pp.previous = temp;

        // Render simulation to current RT (reads nothing from previous — 
        // this is a stateless noise field, not a feedback loop)
        const currentRT = gl.getRenderTarget();
        gl.setRenderTarget(pp.current);
        gl.render(simScene, simCamera);
        gl.setRenderTarget(currentRT);

        // --- UPDATE DISPLAY MATERIAL ---
        displayMaterial.uniforms.uPositions.value = pp.current.texture;

        // --- CAMERA: subtle auto-orbit + mouse tilt ---
        cameraAngle.current += 0.00015;

        // Mouse tilt targets
        const targetRotY = (mouseRef.current.x - 0.5) * 0.08; // [-0.04, 0.04]
        const targetRotX = -(mouseRef.current.y - 0.5) * 0.04; // [-0.02, 0.02]

        // Lerp toward targets
        cameraTargetRotY.current += (targetRotY - cameraTargetRotY.current) * 0.03;
        cameraTargetRotX.current += (targetRotX - cameraTargetRotX.current) * 0.03;

        // Apply to camera
        const cam = state.camera;
        const orbitRadius = 5;
        cam.position.x = Math.sin(cameraAngle.current) * orbitRadius;
        cam.position.y = 2.5;
        cam.position.z = Math.cos(cameraAngle.current) * orbitRadius;
        cam.lookAt(0, 0, 0);
        cam.rotation.x += cameraTargetRotX.current;
        cam.rotation.y += cameraTargetRotY.current;
    });

    // Cleanup
    useEffect(() => {
        return () => {
            rtA.dispose();
            rtB.dispose();
            simMaterial.dispose();
            displayGeometry.dispose();
            displayMaterial.dispose();
        };
    }, [rtA, rtB, simMaterial, displayGeometry, displayMaterial]);

    return (
        <group>
            <points geometry={displayGeometry} material={displayMaterial} />
        </group>
    );
};

export default GPGPUParticleField;
